// Pure shape validation for the two extraction stages (§4 of the plan).
//
// JSON from Claude is prompt-enforced, not schema-enforced (house pattern),
// so AFTER parsing, these deterministic validators check every entry. Entries
// that fail are QUARANTINED into a visible "couldn't interpret" list — never
// silently dropped, never silently "fixed".
//
// Pure module: no React, no Supabase, no fetch — node-testable.

import { normalizeFieldType } from './fmxCustomFieldsApi.js';
import { normalizeFieldset } from './fieldsets.js';
import { mergeCandidates, fieldBySourceItem } from './mergeFields.js';
import { applyVisibilityRules } from './visibilityFormula.js';

// Brace-slice + JSON.parse, tolerant of stray prose around the object
// (matches the parse discipline in pdfExtract.js / equipmentOcr.js).
export function parseJsonLoose(text) {
  if (!text) return null;
  try {
    const first = text.indexOf('{');
    const last = text.lastIndexOf('}');
    const jsonStr = first !== -1 && last > first ? text.slice(first, last + 1) : text;
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

const STAGE1_KINDS = ['question', 'static_text', 'signature', 'table'];
const STAGE1_ANSWER_TYPES = [
  'blank_line', 'multiline_box', 'checkbox', 'checkbox_group', 'radio_group',
  'dropdown', 'date', 'time', 'table', 'attachment', 'signature',
];
const CONFIDENCES = ['high', 'medium', 'low'];

// Validate + normalize a stage-1 (structure) result. Malformed items are
// quarantined with a reason; malformed sections quarantine their items.
// Returns { stage1: { formTitle, sections }, quarantined: [{ raw, reason }] }.
export function validateStage1Shape(parsed) {
  const quarantined = [];
  if (!parsed || typeof parsed !== 'object') {
    return { stage1: { formTitle: null, note: null, sections: [] }, quarantined: [{ raw: parsed, reason: 'not a JSON object' }] };
  }
  const sections = [];
  const seenItemIds = new Set();
  const rawSections = Array.isArray(parsed.sections) ? parsed.sections : [];
  rawSections.forEach((sec, si) => {
    if (!sec || typeof sec !== 'object' || !Array.isArray(sec.items)) {
      quarantined.push({ raw: sec, reason: `section ${si + 1} is not an object with an items array` });
      return;
    }
    const items = [];
    sec.items.forEach((item, ii) => {
      const problems = [];
      if (!item || typeof item !== 'object') problems.push('not an object');
      else {
        if (typeof item.label !== 'string' || !item.label.trim()) problems.push('missing label');
        if (item.kind != null && !STAGE1_KINDS.includes(item.kind)) problems.push(`unknown kind "${item.kind}"`);
        if (item.answerType != null && !STAGE1_ANSWER_TYPES.includes(item.answerType)) problems.push(`unknown answerType "${item.answerType}"`);
        if (item.options != null && (!Array.isArray(item.options) || item.options.some(o => typeof o !== 'string'))) problems.push('options must be an array of strings');
      }
      if (problems.length) {
        quarantined.push({ raw: item, reason: `item ${ii + 1} in section ${si + 1}: ${problems.join('; ')}` });
        return;
      }
      let id = typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `s${si + 1}-q${ii + 1}`;
      while (seenItemIds.has(id)) id = `${id}-dup`; // ids must stay unique — stage 2 references them
      seenItemIds.add(id);
      items.push({
        id,
        kind: STAGE1_KINDS.includes(item.kind) ? item.kind : 'question',
        label: item.label.trim(),
        answerType: item.answerType ?? null,
        options: Array.isArray(item.options) ? item.options : [],
        hasOtherOption: item.hasOtherOption === true,
        required: item.required === true,
        requiredEvidence: item.requiredEvidence ?? null,
        helpText: item.helpText ?? null,
        conditionalText: item.conditionalText ?? null,
        sourcePage: Number.isInteger(item.sourcePage) ? item.sourcePage : null,
        sourceSection: item.sourceSection ?? null,
        confidence: CONFIDENCES.includes(item.confidence) ? item.confidence : 'low',
        note: item.note ?? null,
      });
    });
    sections.push({
      id: typeof sec.id === 'string' && sec.id.trim() ? sec.id.trim() : `s${si + 1}`,
      title: sec.title ?? null,
      instructions: sec.instructions ?? null,
      sourcePage: Number.isInteger(sec.sourcePage) ? sec.sourcePage : null,
      sourceSection: sec.sourceSection ?? null,
      items,
    });
  });
  return {
    // `note` is the model's escape hatch ("not a form", "illegible scan") —
    // surfaced to the user when a read produces zero items.
    stage1: {
      formTitle: parsed.formTitle ?? null,
      note: typeof parsed.note === 'string' && parsed.note.trim() ? parsed.note.trim() : null,
      sections,
    },
    quarantined,
  };
}

// Merge a continuation batch's sections into the accumulated stage-1 result
// (multi-page PDFs: stage 1 runs sequentially with rolling context, and a
// section can span batches). Same section id → items append; new id → section
// appends. Duplicate item ids across batches get a suffix rather than
// clobbering.
export function mergeStage1Batches(accumulated, next) {
  if (!accumulated) return next;
  const out = {
    formTitle: accumulated.formTitle || next.formTitle || null,
    note: accumulated.note || next.note || null,
    sections: accumulated.sections.map(s => ({ ...s, items: [...s.items] })),
  };
  const byId = new Map(out.sections.map(s => [s.id, s]));
  const seenItems = new Set(out.sections.flatMap(s => s.items.map(i => i.id)));
  for (const sec of next.sections || []) {
    const items = sec.items.map(item => {
      let id = item.id;
      while (seenItems.has(id)) id = `${id}-b`;
      seenItems.add(id);
      return { ...item, id };
    });
    const existing = byId.get(sec.id);
    if (existing) existing.items.push(...items);
    else {
      const copy = { ...sec, items };
      out.sections.push(copy);
      byId.set(copy.id, copy);
    }
  }
  return out;
}

// Compact context summary of the sections found so far, carried into the
// next stage-1 batch call so continuations stitch onto the right section.
export function summarizeStage1ForContext(stage1) {
  if (!stage1?.sections?.length) return 'None yet — this is the first batch.';
  return stage1.sections.map(s => {
    const last = s.items[s.items.length - 1];
    return `- ${s.id}: "${s.title || '(untitled)'}" — ${s.items.length} items so far, last item id ${last ? last.id : 'none'}`;
  }).join('\n');
}

export function stage1ItemIds(stage1) {
  const ids = new Set();
  for (const sec of stage1?.sections || []) {
    for (const item of sec.items || []) ids.add(item.id);
  }
  return ids;
}

// Validate + normalize a stage-2 (mapping) result against the confirmed
// fieldsets and the real stage-1 item ids. Every failing entry is quarantined
// with its reason. Returns { stage2: { fields, nativeMappings, prerequisites,
// unsupportedLogic, flags }, quarantined }.
export function validateStage2Shape(parsed, { confirmedFieldsets = [], stage1Ids = new Set() } = {}) {
  const quarantined = [];
  const empty = { fields: [], visibilityRules: [], nativeMappings: [], prerequisites: [], unsupportedLogic: [], flags: [] };
  if (!parsed || typeof parsed !== 'object') {
    return { stage2: empty, quarantined: [{ raw: parsed, reason: 'not a JSON object' }] };
  }

  const checkSource = (entry, problems) => {
    if (typeof entry.sourceItemId !== 'string' || !entry.sourceItemId.trim()) {
      problems.push('missing sourceItemId');
    } else if (stage1Ids.size && !stage1Ids.has(entry.sourceItemId)) {
      problems.push(`sourceItemId "${entry.sourceItemId}" does not exist in the structural read`);
    }
  };

  const fields = [];
  for (const f of Array.isArray(parsed.fields) ? parsed.fields : []) {
    const problems = [];
    if (!f || typeof f !== 'object') problems.push('not an object');
    else {
      checkSource(f, problems);
      if (typeof f.name !== 'string' || !f.name.trim()) problems.push('missing name');
      else if (f.name.trim().length > 200) problems.push('name longer than 200 characters');
      const fieldset = normalizeFieldset(f.fieldset);
      if (!fieldset) problems.push(`unknown fieldset "${f.fieldset}"`);
      else if (confirmedFieldsets.length && !confirmedFieldsets.includes(fieldset)) problems.push(`fieldset "${fieldset}" was not confirmed for this session`);
      if (!normalizeFieldType(f.fieldType)) problems.push(`unknown fieldType "${f.fieldType}"`);
      if (f.options != null && (!Array.isArray(f.options) || f.options.some(o => typeof o !== 'string'))) problems.push('options must be an array of strings');
      if (f.defaults != null && (!Array.isArray(f.defaults) || f.defaults.some(o => typeof o !== 'string'))) problems.push('defaults must be an array of strings');
    }
    if (problems.length) { quarantined.push({ raw: f, bucket: 'fields', reason: problems.join('; ') }); continue; }
    fields.push({
      sourceItemId: f.sourceItemId,
      name: f.name.trim(),
      fieldset: normalizeFieldset(f.fieldset),
      fieldType: normalizeFieldType(f.fieldType),
      isRequired: f.isRequired === true,
      options: Array.isArray(f.options) ? f.options : [],
      allowOtherOption: f.allowOtherOption === true,
      allowMultipleSelections: f.allowMultipleSelections === true,
      allowMultipleLines: f.allowMultipleLines === true,
      disallowNegativeValues: f.disallowNegativeValues === true,
      defaults: Array.isArray(f.defaults) ? f.defaults : [],
      description: f.description ?? null,
      confidence: CONFIDENCES.includes(f.confidence) ? f.confidence : 'low',
      rationale: f.rationale ?? null,
      sourcePage: Number.isInteger(f.sourcePage) ? f.sourcePage : null,
    });
  }

  const simpleBucket = (rawList, bucket, requiredKeys) => {
    const out = [];
    for (const e of Array.isArray(rawList) ? rawList : []) {
      const problems = [];
      if (!e || typeof e !== 'object') problems.push('not an object');
      else {
        checkSource(e, problems);
        for (const k of requiredKeys) {
          if (typeof e[k] !== 'string' || !e[k].trim()) problems.push(`missing ${k}`);
        }
      }
      if (problems.length) quarantined.push({ raw: e, bucket, reason: problems.join('; ') });
      else out.push(e);
    }
    return out;
  };

  // Visibility rules (prompt v2): the AI says WHICH question triggers this
  // one and on what value; code composes the formula (visibilityFormula.js).
  // A rule whose dependsOnSourceItemId doesn't resolve to a real stage-1 item
  // is quarantined rather than silently producing a formula that references
  // nothing.
  const VALID_OPERATORS = [
    'equals', 'notEquals', 'greaterThan', 'greaterOrEqual',
    'lessThan', 'lessOrEqual', 'includes', 'notIncludes',
  ];
  const visibilityRules = [];
  for (const r of Array.isArray(parsed.visibilityRules) ? parsed.visibilityRules : []) {
    const problems = [];
    if (!r || typeof r !== 'object') problems.push('not an object');
    else {
      checkSource(r, problems);
      if (typeof r.dependsOnSourceItemId !== 'string' || !r.dependsOnSourceItemId.trim()) {
        problems.push('missing dependsOnSourceItemId');
      } else if (stage1Ids.size && !stage1Ids.has(r.dependsOnSourceItemId)) {
        problems.push(`dependsOnSourceItemId "${r.dependsOnSourceItemId}" does not exist in the structural read`);
      } else if (r.dependsOnSourceItemId === r.sourceItemId) {
        problems.push('a field cannot depend on itself');
      }
      if (r.operator != null && !VALID_OPERATORS.includes(r.operator)) {
        problems.push(`unknown operator "${r.operator}"`);
      }
    }
    if (problems.length) { quarantined.push({ raw: r, bucket: 'visibilityRules', reason: problems.join('; ') }); continue; }
    visibilityRules.push({
      sourceItemId: r.sourceItemId,
      dependsOnSourceItemId: r.dependsOnSourceItemId,
      operator: VALID_OPERATORS.includes(r.operator) ? r.operator : 'equals',
      value: r.value ?? true,
      rawText: r.rawText ?? null,
      sourcePage: Number.isInteger(r.sourcePage) ? r.sourcePage : null,
    });
  }

  return {
    stage2: {
      fields,
      visibilityRules,
      nativeMappings: simpleBucket(parsed.nativeMappings, 'nativeMappings', ['label', 'mapsTo']),
      prerequisites: simpleBucket(parsed.prerequisites, 'prerequisites', ['kind', 'value']),
      unsupportedLogic: simpleBucket(parsed.unsupportedLogic, 'unsupportedLogic', ['text']),
      flags: simpleBucket(parsed.flags, 'flags', ['issue']),
    },
    quarantined,
  };
}

// Build the initial editable draft from a validated stage-2 result. Every
// draft field carries its sourceItemId back into the immutable snapshot plus
// a provenance marker ('ai' | 'edited' | 'added'). sortPosition follows
// stage-1 document order within each fieldset.
//
// The same question asked under several request-type sections is MERGED into
// one field here (rules R1/R2/R7 — see mergeFields.js); the stage-2 snapshot
// itself stays untouched, so per-field revert still resolves through the
// primary sourceItemId. Groups that can't be safely merged come back as
// `mergeConflicts` and stay as separate fields for a human to resolve.
//
// Visibility rules become formula text (R4-R6 — see visibilityFormula.js).
export function draftFromStage2(stage2, stage1) {
  const orderOf = new Map();
  let n = 0;
  for (const sec of stage1?.sections || []) {
    for (const item of sec.items || []) orderOf.set(item.id, n++);
  }
  const { fields: merged, conflicts } = mergeCandidates(stage2?.fields || [], stage1);
  const byFieldset = new Map();
  const sorted = [...merged].sort((a, b) =>
    (orderOf.get(a.sourceItemId) ?? 1e9) - (orderOf.get(b.sourceItemId) ?? 1e9)
  );
  const fields = sorted.map((f, i) => {
    const pos = byFieldset.get(f.fieldset) || 0;
    byFieldset.set(f.fieldset, pos + 1);
    return {
      id: `fld-${i + 1}`,
      provenance: 'ai',
      sortPosition: pos,
      ...f,
    };
  });

  const withFormulas = applyVisibilityRules(
    fields, stage2?.visibilityRules, fieldBySourceItem(fields)
  );

  return {
    fields: withFormulas.fields,
    dismissed: [],   // [{ bucket, sourceItemId, reason }]
    mergeConflicts: conflicts,
    unresolvedVisibility: withFormulas.unresolved,
  };
}
